import React from 'react';
import { Box, Typography, Avatar, Button, Link as MuiLink, useTheme } from '@mui/material';
import { lighten } from '@mui/system';
import getIconForType from '../../utils/icons/getIconForType';
import placeholderImage from '../../images/placeholder-grey-image.jpg';
import avatarPlaceholder from '../../images/avatar-cropped.png';
import { QrCodeIcon } from 'icons/materialIcons';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import { useEventTracking } from '../../services/eventTrackingService';
import { formatPhoneNumber, isPhoneContactType } from '../../utils/phoneUtils';

const CardDesign3 = ({
    cardId,
    cardFullName,
    cardJobTitle,
    cardCompany,
    cardTheme,
    cardProfilePictureUrl,
    cardCoverPhotoUrl,
    cardCompanyLogoUrl,
    contextCardLayout,
    cardLinkColor,
    cardLocation,
    cardBio,
    cardPronoun,
    formattedPronoun,
    visibleContactLinks,
    previewContactLink,
    handleSaveContact
}) => {
    const theme = useTheme();
    const cardThemeValues = theme.card;
    const design3Theme = cardThemeValues.cardDesign3;

    const tracking = useEventTracking();

    const cardAccentColor = cardTheme || cardLinkColor || '#F06411';
    const secondaryAccentColor = lighten(cardAccentColor, 0.9);
    const gradientColor = lighten(cardAccentColor, 0.2);
    const handleLinkClick = (link, linkType) => {
        if (tracking && link?.details) {
            tracking.trackLinkTap({
                linkType: linkType,
                linkUrl: link.details.linkUrl,
                linkTitle: link.details.linkTitle || linkType,
                cardId: cardId
            });
        }

        if (link?.details?.formattedLink) {
            window.open(link.details.formattedLink, '_blank');
        } else if (link?.details?.linkUrl) {
            const url = link.details.linkUrl.startsWith('http') ?
                link.details.linkUrl :
                `https://${link.details.linkUrl}`;
            window.open(url, '_blank');
        }
    };

    const allLinks = React.useMemo(() => {
        if (!previewContactLink) return visibleContactLinks || [];
        const linkExists = (visibleContactLinks || []).some(
            link => link.type === previewContactLink.type &&
                link.details?.linkUrl === previewContactLink.details?.linkUrl
        );
        return linkExists ? visibleContactLinks : [...(visibleContactLinks || []), previewContactLink];
    }, [visibleContactLinks, previewContactLink]);

    const contactLinks = React.useMemo(() => {
        return (allLinks || []).filter(link =>
            ['email', 'phone', 'whatsapp', 'sms', 'call'].includes(link.type?.toLowerCase())
        );
    }, [allLinks]);

    const socialLinks = React.useMemo(() => {
        return (allLinks || []).filter(link =>
            !['email', 'phone', 'whatsapp', 'sms', 'call', 'website', 'address', 'map'].includes(link.type?.toLowerCase())
        );
    }, [allLinks]);

    const websiteLinks = React.useMemo(() => {
        return (allLinks || []).filter(link =>
            ['website', 'address', 'map'].includes(link.type?.toLowerCase())
        );
    }, [allLinks]);
    const renderLinkCard = (link, index) => {
        if (!link || !link.details?.linkUrl) return null;

        return (
            <Box
                key={index}
                onClick={() => handleLinkClick(link, link.type)}
                sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'block',
                    mb: 2,
                    cursor: 'pointer'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        backgroundColor: secondaryAccentColor,
                        borderRadius: '0.5rem',
                        transition: 'transform 0.2s',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: design3Theme.contactIcon.width,
                            height: design3Theme.contactIcon.height,
                            borderRadius: design3Theme.contactIcon.borderRadius,
                            backgroundColor: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            p: 1
                        }}
                    >
                        {link.details?.icon ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundImage: `url(${link.details.icon})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    borderRadius: '0.25rem',
                                }}
                            />
                        ) : React.cloneElement(getIconForType(link.type), {
                            sx: {
                                color: cardAccentColor,
                                fontSize: design3Theme.contactIconInner.fontSize,
                            },
                        })}
                    </Box>
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{
                                textTransform: design3Theme.contactLabel.textTransform,
                                color: design3Theme.contactLabel.color,
                                fontSize: design3Theme.contactLabel.fontSize,
                                fontWeight: design3Theme.contactLabel.fontWeight,
                                display: design3Theme.contactLabel.display
                            }}
                        >
                            {link.type}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: design3Theme.contactValue.fontWeight,
                                fontSize: design3Theme.contactValue.fontSize,
                                wordBreak: design3Theme.contactValue.wordBreak,
                            }}
                        >
                            {(() => {
                                if (isPhoneContactType(link.type) && link.details?.linkUrl) {
                                    return formatPhoneNumber(link.details.linkUrl, 'SG');
                                }
                                return link.details.linkTitle || link.details.linkUrl;
                            })()}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderSocialLinks = () => {
        if (!socialLinks || socialLinks.length === 0) return null;

        return (
            <Box sx={{ mt: 4, mb: 3 }}>
                <Typography
                    variant="body2"
                    sx={{
                        textAlign: design3Theme.socialTitle.textAlign,
                        color: design3Theme.socialTitle.color,
                        textTransform: design3Theme.socialTitle.textTransform,
                        fontSize: design3Theme.socialTitle.fontSize,
                        mb: 2
                    }}
                >
                    Connect on Social
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 2,
                        px: 2
                    }}
                >
                    {socialLinks.map((link, index) => (
                        link && link.details?.linkUrl ? (
                            <MuiLink
                                key={index}
                                href={link.details.formattedLink}
                                target="_blank"
                                sx={{
                                    textDecoration: 'none'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: design3Theme.socialIcon.width,
                                        height: design3Theme.socialIcon.height,
                                        borderRadius: design3Theme.socialIcon.borderRadius,
                                        background: `linear-gradient(135deg, ${cardAccentColor} 0%, ${gradientColor} 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: `0 4px 14px ${lighten(cardAccentColor, 0.7)}`,
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-3px)',
                                        },
                                    }}
                                >
                                    {link.details?.icon ? (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: design3Theme.socialIcon.borderRadius,
                                                overflow: 'hidden',
                                                backgroundImage: `url(${link.details.icon})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                            }}
                                        />
                                    ) : React.cloneElement(getIconForType(link.type), {
                                        sx: {
                                            color: design3Theme.socialIconInner.color,
                                            fontSize: design3Theme.socialIconInner.fontSize,
                                        },
                                    })}
                                </Box>
                            </MuiLink>
                        ) : null
                    ))}
                </Box>
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                maxWidth: cardThemeValues.container.maxWidth,
                boxSizing: 'border-box',
                padding: cardThemeValues.container.padding,
                margin: 'auto',
                position: 'relative',
                fontFamily: '"Kumbh Sans", sans-serif',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    position: 'relative',
                    boxShadow: design3Theme.mainContainer.boxShadow,
                    borderRadius: design3Theme.mainContainer.borderRadius,
                    overflow: design3Theme.mainContainer.overflow
                }}
            >
                <Box
                    sx={{
                        height: design3Theme.header.height,
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 0,
                            backgroundImage: `linear-gradient(135deg, ${cardAccentColor} 0%, ${gradientColor} 100%)`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            padding: design3Theme.header.padding,
                            position: 'relative',
                            zIndex: 2
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: '24px'
                        }}>
                            <MuiLink
                                href={FRONTEND_BASE_URL}
                                target="_blank"
                                sx={{
                                    px: 2,
                                    py: 2,
                                    borderRadius: '8px',
                                    border: '1px solid white',
                                    color: 'white',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    display: 'block'
                                }}
                            >
                                Get Your Card
                            </MuiLink>

                            <Button
                                sx={{
                                    minWidth: 'auto',
                                    p: '8px',
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        backgroundColor: 'white',
                                    }
                                }}
                            >
                                <QrCodeIcon
                                    fontSize="small"
                                    sx={{
                                        color: cardAccentColor
                                    }}
                                />
                            </Button>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        backgroundColor: cardTheme ? lighten(cardTheme, 0.97) : '#fff',
                        color: cardTheme ? '#333' : '#333',
                        borderRadius: design3Theme.profileCard.borderRadius,
                        padding: design3Theme.profileCard.padding,
                        display: 'flex',
                        boxShadow: design3Theme.profileCard.boxShadow,
                        width: design3Theme.profileCard.width,
                        maxWidth: design3Theme.profileCard.maxWidth,
                        position: 'absolute',
                        top: design3Theme.profileCard.top,
                        left: 0,
                        right: 0,
                        margin: '0 auto',
                        zIndex: design3Theme.profileCard.zIndex,
                        marginBottom: design3Theme.profileCard.marginBottom,
                    }}
                >
                    <Box
                        sx={{
                            width: design3Theme.avatar.width,
                            height: contextCardLayout === 'portrait' ? '100%' : 'auto',
                        }}
                    >
                        <Avatar
                            src={cardProfilePictureUrl || avatarPlaceholder}
                            sx={{
                                width: '100%',
                                height: '100%',
                                minHeight: design3Theme.avatar.minHeight,
                                border: design3Theme.avatar.border,
                                boxShadow: design3Theme.avatar.boxShadow,
                                borderRadius: design3Theme.avatar.borderRadius
                            }}
                            variant={contextCardLayout === 'portrait' ? 'square' : 'rounded'}
                        />
                    </Box>

                    <Box
                        sx={{
                            width: design3Theme.profileInfo.width,
                            padding: design3Theme.profileInfo.padding,
                            position: 'relative',
                        }}
                    >
                        {cardCompanyLogoUrl && (
                            <Avatar
                                src={cardCompanyLogoUrl}
                                sx={{
                                    width: design3Theme.companyLogo.size,
                                    height: design3Theme.companyLogo.size,
                                    border: design3Theme.companyLogo.border,
                                    boxShadow: design3Theme.companyLogo.boxShadow,
                                    position: design3Theme.companyLogo.position,
                                    top: design3Theme.companyLogo.top,
                                    right: design3Theme.companyLogo.right,
                                }}
                            />
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: design3Theme.nameTypography.fontWeight,
                                    fontSize: design3Theme.nameTypography.fontSize,
                                    marginBottom: design3Theme.nameTypography.marginBottom,
                                    wordBreak: design3Theme.nameTypography.wordBreak,
                                    fontFamily: '"Kumbh Sans", sans-serif',
                                    marginRight: '0',
                                }}
                            >
                                {cardFullName}
                            </Typography>
                            {/* {cardPronoun && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.8rem',
                                        opacity: 0.8,
                                        fontFamily: '"Kumbh Sans", sans-serif',
                                        fontWeight: 400,
                                        alignSelf: 'center',
                                        marginTop: '2px',
                                    }}
                                >
                                    ({formattedPronoun})
                                </Typography>
                            )} */}
                        </Box>

                        {cardJobTitle && (
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: design3Theme.jobTitleTypography.fontWeight,
                                    fontSize: design3Theme.jobTitleTypography.fontSize,
                                    marginBottom: design3Theme.jobTitleTypography.marginBottom,
                                    wordBreak: design3Theme.jobTitleTypography.wordBreak,
                                    fontFamily: '"Kumbh Sans", sans-serif',
                                }}
                            >
                                {cardJobTitle}
                            </Typography>
                        )}

                        {cardCompany && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: design3Theme.companyTypography.fontSize,
                                    marginBottom: design3Theme.companyTypography.marginBottom,
                                    wordBreak: design3Theme.companyTypography.wordBreak,
                                    fontFamily: '"Kumbh Sans", sans-serif',
                                }}
                            >
                                {cardCompany}
                            </Typography>
                        )}

                        {cardLocation && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: design3Theme.locationTypography.fontSize,
                                    opacity: design3Theme.locationTypography.opacity,
                                    wordBreak: design3Theme.locationTypography.wordBreak,
                                    fontFamily: '"Kumbh Sans", sans-serif',
                                }}
                            >
                                {cardLocation}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        backgroundColor: design3Theme.aboutContainer.backgroundColor,
                        borderTopLeftRadius: design3Theme.aboutContainer.borderTopLeftRadius,
                        borderTopRightRadius: design3Theme.aboutContainer.borderTopRightRadius,
                        paddingTop: design3Theme.aboutContainer.paddingTop,
                        paddingBottom: design3Theme.aboutContainer.paddingBottom,
                        position: 'relative',
                        zIndex: design3Theme.aboutContainer.zIndex,
                        marginTop: design3Theme.aboutContainer.marginTop,
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: secondaryAccentColor,
                            margin: design3Theme.tabs.margin,
                            width: design3Theme.tabs.width,
                            borderRadius: design3Theme.tabs.borderRadius,
                            padding: design3Theme.tabs.padding,
                            display: 'flex',
                            justifyContent: 'space-around',
                            boxShadow: design3Theme.tabs.boxShadow,
                            position: 'relative',
                            zIndex: design3Theme.tabs.zIndex,
                            fontFamily: '"Kumbh Sans", sans-serif',
                        }}
                    >
                        <Box
                            sx={{
                                padding: design3Theme.tabButton.padding,
                                backgroundColor: '#fff',
                                borderRadius: design3Theme.tabButton.borderRadius,
                                fontWeight: design3Theme.tabButton.fontWeight,
                                fontSize: design3Theme.tabButton.fontSize,
                                flex: design3Theme.tabButton.flex,
                                textAlign: design3Theme.tabButton.textAlign
                            }}
                        >
                            Contact
                        </Box>
                        <Box
                            sx={{
                                padding: design3Theme.tabButton.padding,
                                fontWeight: design3Theme.tabButton.fontWeight,
                                fontSize: design3Theme.tabButton.fontSize,
                                flex: design3Theme.tabButton.flex,
                                textAlign: design3Theme.tabButton.textAlign
                            }}
                        >
                            Social
                        </Box>
                        <Box
                            sx={{
                                padding: design3Theme.tabButton.padding,
                                fontWeight: design3Theme.tabButton.fontWeight,
                                fontSize: design3Theme.tabButton.fontSize,
                                flex: design3Theme.tabButton.flex,
                                textAlign: design3Theme.tabButton.textAlign
                            }}
                        >
                            Services
                        </Box>
                    </Box>

                    {cardBio && (
                        <Box sx={{ padding: design3Theme.bioSection.padding }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: design3Theme.bioTypography.fontSize,
                                    lineHeight: design3Theme.bioTypography.lineHeight,
                                    textAlign: design3Theme.bioTypography.textAlign,
                                    color: design3Theme.bioTypography.color,
                                    whiteSpace: design3Theme.bioTypography.whiteSpace,
                                    fontFamily: '"Kumbh Sans", sans-serif',
                                }}
                            >
                                {cardBio}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ padding: design3Theme.aboutSection.padding }}>
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: design3Theme.sectionTitle.textAlign,
                                color: design3Theme.sectionTitle.color,
                                textTransform: design3Theme.sectionTitle.textTransform,
                                fontSize: design3Theme.sectionTitle.fontSize,
                                mb: design3Theme.sectionTitle.marginBottom,
                                fontFamily: '"Kumbh Sans", sans-serif',
                            }}
                        >
                            About {cardFullName.split(' ')[0]}
                        </Typography>

                        {contactLinks.map((link, index) => renderLinkCard(link, index))}

                        {websiteLinks.map((link, index) => renderLinkCard(link, index))}

                        {renderSocialLinks()}
                    </Box>

                    <Box
                        sx={{
                            padding: design3Theme.saveButtonContainer.padding,
                            display: design3Theme.saveButtonContainer.display,
                            justifyContent: design3Theme.saveButtonContainer.justifyContent
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSaveContact}
                            sx={{
                                width: design3Theme.saveButton.width,
                                borderRadius: design3Theme.saveButton.borderRadius,
                                padding: design3Theme.saveButton.padding,
                                fontWeight: design3Theme.saveButton.fontWeight,
                                textTransform: design3Theme.saveButton.textTransform,
                                fontSize: design3Theme.saveButton.fontSize,
                                background: `linear-gradient(135deg, ${cardAccentColor} 0%, ${gradientColor} 100%)`,
                                boxShadow: `0 4px 14px ${lighten(cardAccentColor, 0.7)}`,
                                transition: design3Theme.saveButton.transition,
                                fontFamily: '"Kumbh Sans", sans-serif',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    background: `linear-gradient(135deg, ${cardAccentColor} 0%, ${gradientColor} 100%)`,
                                },
                            }}
                        >
                            Save Contact
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default CardDesign3;